import { computed, defineComponent, onMounted, ref, useTemplateRef, watch } from 'vue';
import * as KlipApi from '@/api/klip-api.proxy';
import QueryFilter from '@/app/shared/helpers/queryFilter';
import {useRoute, useRouter} from '@/router/router-vue3';
import KlSearchMaprequests, {
    castStringToSearchField,
    MapRequestSearchField
} from '@/app/shared/components/kl-search-maprequests/kl-search-maprequests';
import { escape } from 'html-escaper';
import DateUtil from '@/app/shared/helpers/date-util';
import { handleAbortError, KlAbortController } from '@/app/shared/helpers/kl-abort-controller';
import { useKlipApiProxy } from '@/plugins/proxy-client';
import { RouteRecordRaw } from 'vue-router';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlPdaMaprequests',
    props: {
        zoneId: {
            type: String,
            default: null,
            required: true
        },
        zoneDetailView: {
            type: KlipApi.ZoneDetailView,
            default: null,
            required: true
        },
        parentOrganisationId: {
            type: String,
            default: null,
            required: true
        },
        maprequestDetailRoute: {
            type: Object as () => RouteRecordRaw,
            default: null,
            required: true
        }
    },
    setup: function (props) {

        const _pda_GetMapRequests_AbortController = new KlAbortController();
        const _pda_GetMapRequestsCount_AbortController = new KlAbortController();

        const route = useRoute();
        const router = useRouter();
        const proxy = useKlipApiProxy();
        const queryFilter = new QueryFilter(router);

        const initialMappingFromQuery = ref<boolean>(true);
        const mapRequests = ref<KlipApi.IMapRequestListItem2[]>([]);
        const mapRequestsMeta = ref<{ totalRows: number, resultsPerPage: number, currentPage: number | undefined }>({ totalRows: 0, resultsPerPage: 50, currentPage: 1 });
        const mapRequestsFetching = ref<boolean>(true);
        const mapRequestsColumns = ref<KlipApi.IColumn[]>([]);
        const mapRequestsInput = ref<KlipApi.IGetPdaMapRequestsInput>(null);
        const mapRequestsSearchFields = ref<KlipApi.CodeListValueOrder[]>([]);
        const defaultFilter = ref<MapRequestSearchField>(new MapRequestSearchField());

        const searchMaprequests = useTemplateRef<InstanceType<typeof KlSearchMaprequests>>('searchMaprequests');

        let oldMapRequestInput: string;


        const routePdaMapRequest = computed((): RouteRecordRaw => {
            return props.maprequestDetailRoute;
        });

        const queryTypeDefinition = {
            orderField: KlipApi.OrderField,
            orderDirection: KlipApi.OrderDirection,
            searchField: KlipApi.SearchField,
            startDate: 'Date',
            endDate: 'Date'
        };

        const defaultSearchOption = computed(() => {
            // hack for the generic component to emit search back, so that our component reacts to it
            //and have a working search for the next filters
            return "1";
        });

        const _updateQuery = () => {
            queryFilter.mapQuery(route, mapRequestsInput.value);
        }

        const search = (searchFields: MapRequestSearchField) => {
            if (!initialMappingFromQuery.value) {
                mapRequestsFetching.value = true;
                mapRequestsInput.value = {
                    zoneId: props.zoneId,
                    parentOrganisationId: props.parentOrganisationId,
                    offset: 0,
                    limit: mapRequestsInput.value.limit,
                    orderDirection: mapRequestsInput.value.orderDirection,
                    orderField: mapRequestsInput.value.orderField,
                    query: searchFields.keyword,
                    searchField: castStringToSearchField(searchFields.searchField),
                    startDate: DateUtil.formatForApi(searchFields.date.from, 'ISO'),
                    endDate: DateUtil.formatForApi(searchFields.date.to, 'ISO'),
                    zoneGeometry: searchFields.location ? JSON.stringify(searchFields.location) : null
                };

                _updateQuery();
            }

            initialMappingFromQuery.value = false;
        }

        const _fetchMapRequestsCount = async () => {
            try {
                const input = KlipApi.GetPdaMapRequestsInput.fromJS(mapRequestsInput.value);
                const response = await proxy.pda_GetMapRequestsCount(input, _pda_GetMapRequestsCount_AbortController.abortAndCreateNewSignal());

                mapRequestsMeta.value.totalRows = response.result;
            }
            catch (err) {
                handleAbortError(err);
            }
        }

        const _fetchMapRequests = async () => {
            try {
                const input = KlipApi.GetPdaMapRequestsInput.fromJS(mapRequestsInput.value);
                const response = await proxy.pda_GetMapRequests(input, _pda_GetMapRequests_AbortController.abortAndCreateNewSignal());

                if (!response.result) { return; }

                mapRequests.value = response.result.map((mapRequest) => ({
                    id: mapRequest.mapRequestId,
                    reference: mapRequest.reference,
                    requestor: mapRequest.requestor,
                    dateReceived: DateUtil.formatDate(mapRequest.dateReceived),
                }));
            }
            catch (err) {
                handleAbortError(err);
            }
        }

        const pagerClicked = (page: number) => {
            mapRequestsFetching.value = true;
            mapRequestsInput.value.offset = mapRequestsMeta.value.resultsPerPage * (page - 1);
            queryFilter.mapQuery(route, mapRequestsInput.value);
        }

        const columnClicked = (column: KlipApi.Column) => {
            mapRequestsInput.value.offset = 0;
            mapRequestsInput.value.orderField = KlipApi.OrderField2[column.key[0].toUpperCase() + column.key.substring(1) as keyof typeof KlipApi.OrderField2];
            mapRequestsInput.value.orderDirection = column.direction;
            queryFilter.mapQuery(route, mapRequestsInput.value);
        }

        const clearSearch = () => {
            searchMaprequests.value.reset();
        }

        const onMapRequestsInputChanged = async (mapRequestInput: KlipApi.IGetPdaMapRequestsInput) => {
            if (!mapRequestInput) {
                return;
            }
            const mapRequestInputJson = JSON.stringify(mapRequestInput);
            if (mapRequestInputJson === oldMapRequestInput) {
                return;
            }

            mapRequestsFetching.value = true;
            await _fetchMapRequestsCount();
            await _fetchMapRequests();
            mapRequestsFetching.value = false;

            _updateQuery();

            oldMapRequestInput = mapRequestInputJson;
        }

        const parseCell = (row: IRow, column: KlipApi.IColumn): { template: string } => {
            const cellValue = escape(row[column.key]);
            if (row.id) {
                const routeParams = {
                    id: row.id,
                }
                return { template: `<kl-router-link :to='${JSON.stringify(routePdaMapRequest.value)}' :params='${JSON.stringify(routeParams)}'>${cellValue}</kl-router-link>` };
            } else {
                return { template: `<div>${cellValue}</div>` };
            }
        }

        watch(
            mapRequestsInput,
            async (newValue, oldValue) => {
                // note: don't use oldValue to determine change
                // for some strange reason, newValue and oldValue are always equal here
                await onMapRequestsInputChanged(newValue);
            },
            { immediate: true, deep: true });

        onMounted(async () => {
            mapRequestsInput.value = queryFilter.mapFilterFromQuery(route, props.zoneDetailView.getPdaMapRequestsInput, { searchField: 2 }, queryTypeDefinition);
            mapRequestsInput.value.parentOrganisationId = props.parentOrganisationId;
            mapRequestsMeta.value.currentPage = Math.ceil(mapRequestsInput.value.offset / mapRequestsMeta.value.resultsPerPage) + 1;
            mapRequestsColumns.value = props.zoneDetailView.mapRequestColumns.map((column) => ({
                ...column,
                parser: parseCell,
            }));

            queryFilter.mapColumnsFromQuery(route, mapRequestsColumns.value, KlipApi.OrderField2);
            mapRequestsSearchFields.value = props.zoneDetailView.searchFields;

            defaultFilter.value = {
                searchField: mapRequestsInput.value.searchField ? KlipApi.SearchField[mapRequestsInput.value.searchField] : null,
                keyword: mapRequestsInput.value.query,
                date: {
                    from: DateUtil.parseToDate(mapRequestsInput.value.startDate),
                    to: DateUtil.parseToDate(mapRequestsInput.value.endDate),
                },
                location: mapRequestsInput.value.zoneGeometry ? JSON.parse(mapRequestsInput.value.zoneGeometry) : null
            };
        });

        return {
            search,
            defaultFilter,
            defaultSearchOption,
            mapRequests,
            mapRequestsSearchFields,
            mapRequestsMeta,
            mapRequestsColumns,
            mapRequestsFetching,
            routePdaMapRequest,
            pagerClicked,
            columnClicked,
            clearSearch
        };
    }
})
